import React__default, { useMemo, useState, useCallback, useRef, useEffect } from 'react';
import classNames from 'classnames';
import '../../../hooks/index.js';
import '../../../utils/index.js';
import { useMenuContext } from '../MenuContext.js';
import { KEYS, DATA_TESTID_SUFFIXES as DATA_TESTID_SUFFIXES$1 } from '../constants.js';
import { DATA_TESTID_SUFFIXES } from '../../Select/constants.js';
import styles from '../Menu2.js';
import useSelectContext from '../../Select/SelectContext.js';
import useFocusVirtualBoundary from './useFocusVirtualBoundary.js';
import useControllableState from '../../../hooks/useControllableState/useControllableState.js';
import useTranslation from '../../../utils/Translation/useTranslation.js';
import useEventListener from '../../../hooks/useEventListener/useEventListener.js';

// abstracts the logic required for searching by both `MenuPane` and `SubMenu`
// creates a searchBarConfig, which maps to an instance of `MenuSearch`
// returns values that are necessary for use within pane
function usePaneSearch(_ref) {
  let {
    isSearchable,
    externalSearchValue,
    onSearchValueChange,
    items,
    animatedLeft,
    animatedRight,
    menuParentDataTestId,
    id,
    flyoutNode,
    children = [],
    isSearchFilteringDisabled,
    backButtonRef,
    closeSubmenu,
    isVirtualized
  } = _ref;
  const withinSelect = useSelectContext(true) !== null;
  const [internalSearchValue, setSearchValue] = useControllableState(externalSearchValue, onSearchValueChange, externalSearchValue);
  const {
    translate
  } = useTranslation();
  const noMatchesText = translate('menu.noItemsToDisplay');
  const searchPlaceholder = translate('searchText');
  const filteredChildren = useMemo(() => (Array.isArray(children) ? children : [children]).filter(_ref2 => {
    var _props$label, _searchMatcher$toLowe;
    let {
      props
    } = _ref2;
    if (!props || !internalSearchValue || isSearchFilteringDisabled) {
      return true;
    }
    const searchMatcher = (_props$label = props.label) !== null && _props$label !== void 0 ? _props$label : typeof props.children === 'string' ? props.children : undefined;
    return searchMatcher === null || searchMatcher === void 0 || (_searchMatcher$toLowe = searchMatcher.toLowerCase) === null || _searchMatcher$toLowe === void 0 ? void 0 : _searchMatcher$toLowe.call(searchMatcher).includes(internalSearchValue.toLowerCase());
  }), [internalSearchValue, children, isSearchFilteringDisabled]);
  const focusVirtualBoundary = useFocusVirtualBoundary(items, backButtonRef);
  const [searchInput, setSearchInput] = useState(null);
  const [searchElement, setSearchElement] = useState(null);
  const searchRef = useCallback(node => {
    var _node$querySelector;
    setSearchElement(node);
    setSearchInput((_node$querySelector = node === null || node === void 0 ? void 0 : node.querySelector('input')) !== null && _node$querySelector !== void 0 ? _node$querySelector : null);
  }, []);
  const getFirstVisibleItem = () => {
    var _backButtonRef$curren, _items$find;
    return (_backButtonRef$curren = backButtonRef === null || backButtonRef === void 0 ? void 0 : backButtonRef.current) !== null && _backButtonRef$curren !== void 0 ? _backButtonRef$curren : (_items$find = items.find(item => item.element.current)) === null || _items$find === void 0 ? void 0 : _items$find.element.current;
  };
  const getLastVisibleItem = () => {
    var _findLast$element$cur, _findLast;
    return (// our tsconfig does not target a recent enough version to use `Array.prototype.findLast`
      (_findLast$element$cur = (_findLast = items.findLast(item => item.element.current)) === null || _findLast === void 0 ? void 0 : _findLast.element.current) !== null && _findLast$element$cur !== void 0 ? _findLast$element$cur : backButtonRef === null || backButtonRef === void 0 ? void 0 : backButtonRef.current
    );
  };
  useEventListener(flyoutNode, 'keydown', e => {
    if (KEYS.NAVIGATE_UP.includes(e.key) && e.target === getFirstVisibleItem() || KEYS.NAVIGATE_DOWN.includes(e.key) && e.target === getLastVisibleItem()) {
      searchInput === null || searchInput === void 0 || searchInput.focus();
    }
  }, {
    isDisabled: !isSearchable
  });
  const lastestKeyPressedInSearchInput = useRef();
  useEventListener(searchInput, 'keydown', e => {
    var _e$target;
    lastestKeyPressedInSearchInput.current = e.key;
    if ([...KEYS.NAVIGATE_DOWN, ...KEYS.NAVIGATE_TOP].includes(e.key)) {
      // prevent scrolling
      e.preventDefault();
      if (isVirtualized) {
        focusVirtualBoundary('top');
      } else {
        var _getFirstVisibleItem;
        (_getFirstVisibleItem = getFirstVisibleItem()) === null || _getFirstVisibleItem === void 0 || _getFirstVisibleItem.focus();
      }
    } else if ([...KEYS.NAVIGATE_UP, ...KEYS.NAVIIGATE_BOTTOM].includes(e.key)) {
      // prevent scrolling
      e.preventDefault();
      if (isVirtualized) {
        focusVirtualBoundary('bottom');
      } else {
        var _getLastVisibleItem;
        (_getLastVisibleItem = getLastVisibleItem()) === null || _getLastVisibleItem === void 0 || _getLastVisibleItem.focus();
      }
    } else if (KEYS.CLOSE_SUBMENU.includes(e.key) && ((_e$target = e.target) === null || _e$target === void 0 ? void 0 : _e$target.selectionStart) === 0) {
      closeSubmenu === null || closeSubmenu === void 0 || closeSubmenu(e);
    } else if (KEYS.IGNORED_BY_SEARCH_INPUT.includes(e.key)) {
      e.preventDefault();
    }
  });
  useEventListener(searchInput, 'blur', () => {
    const refocus = ![...KEYS.NAVIGATE_DOWN, ...KEYS.NAVIGATE_UP, ...KEYS.NAVIGATE_TOP, ...KEYS.NAVIIGATE_BOTTOM].includes(lastestKeyPressedInSearchInput.current) && !animatedLeft && !animatedRight && searchInput;
    lastestKeyPressedInSearchInput.current = undefined;
    if (refocus) {
      searchInput.focus();
    }
  });
  const {
    searchBarConfigs,
    setSearchBarConfigs
  } = useMenuContext();
  const onInteractOutside = useCallback(e => searchBarConfigs.some(config => {
    var _config$searchElement;
    return (_config$searchElement = config.searchElement) === null || _config$searchElement === void 0 ? void 0 : _config$searchElement.contains(e.target);
  }) && e.preventDefault(), [searchBarConfigs]);
  useEffect(() => {
    if (!isSearchable) {
      return;
    }
    setSearchBarConfigs(configs => {
      const configsClone = [...configs];
      const updatedConfig = {
        searchValue: internalSearchValue,
        isInteractive: !animatedLeft && !animatedRight,
        animatedLeft,
        animatedRight,
        dataTestId: menuParentDataTestId === null || menuParentDataTestId === void 0 ? void 0 : menuParentDataTestId.replace(withinSelect ? DATA_TESTID_SUFFIXES.SELECT_PANE : DATA_TESTID_SUFFIXES$1.MENU_PANE, withinSelect ? DATA_TESTID_SUFFIXES.SELECT_SEARCH : DATA_TESTID_SUFFIXES$1.MENU_SEARCH),
        setSearchValue,
        ref: searchRef,
        searchElement,
        id,
        searchPlaceholder
      };
      const configIndex = configs.findIndex(config => config.id === id);
      if (configIndex === -1) {
        configsClone.push(updatedConfig);
      } else {
        configsClone[configIndex] = updatedConfig;
      }
      return configsClone;
    });
  }, [isSearchable, searchPlaceholder, setSearchBarConfigs, internalSearchValue, animatedLeft, animatedRight, menuParentDataTestId, setSearchValue, searchRef, searchElement, id, withinSelect]);
  useEffect(() => () => setSearchBarConfigs(configs => {
    const configIndex = configs.findIndex(config => config.id === id);
    if (configIndex === -1) {
      return configs;
    }
    const configsClone = [...configs];
    configsClone.splice(configIndex, 1);
    return configsClone;
  }),
  // Omitted dependencies because we only want this to run on unmount
  // eslint-disable-next-line react-hooks/exhaustive-deps
  []);
  const noSearchResultsNode = filteredChildren.length === 0 && /*#__PURE__*/React__default.createElement("div", {
    className: classNames(styles.menuItem, styles.noItems),
    "aria-live": "assertive",
    role: "menuitem"
  }, noMatchesText);
  return {
    internalSearchValue,
    noSearchResultsNode,
    onInteractOutside,
    filteredChildren
  };
}

export { usePaneSearch as default };
